import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Image, Link, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { LocalHeaderDescriptionProps } from '../LocalHeader.types';
import {
  baiduMapURL,
  chineseLocations,
  mapURL,
  analyticsTrackingIdentifier,
  analyticsTrackingUniqueDescription,
} from '../../../modules/utils/constants/constants';
import { convertPhoneNumberToDE } from '../../../modules/utils/common';
import { PageParamsContext } from '../../../modules/context/PageContext';
import { LocalHeaderAccuWeather } from './LocalHeaderAccuWeather';
import clsx from 'clsx';
import { useSession } from '@marriott/mi-headless-utils';

export const LocalHeaderDescription: FC<LocalHeaderDescriptionProps> = pageProps => {
  const { preProcessorResponse } = useContext(PageParamsContext);
  const { data = null } = pageProps;
  const { state = '' } = preProcessorResponse ?? {};
  const bookYourStayButtonRef = useRef<HTMLDivElement | null>(null);
  const isMobileAndAboveViewPort = useCheckBreakpoint('viewportM'); // check if viewport is Mobile and above
  const [showAccuWeatherInL1] = useState(
    pageProps?.showWeatherInL1 &&
      (pageProps?.showWeatherInSelectedStates?.includes(state?.toString().toLowerCase()) || pageProps?.isAuthorMode)
  );
  const { datalayer } = useSession();

  const baiduMap = async () => {
    try {
      const response = await fetch('/services/marriott-hws/getBaiduMapsApiKey.json', {
        method: 'GET',
        headers: {
          'Content-Type': 'text/plain',
        },
      });
      if (response.status === 200) {
        const data = await response.json();
        const apiKey = atob(data?.apiKey);
        const script = document.createElement('script');
        script.src = `https://api.map.baidu.com/getscript?v=2.0&ak=${apiKey}`;
        script.id = 'baidu-script';
        script.defer = true;

        // Append the 'script' element to 'head'
        document.head.appendChild(script);
        script.addEventListener('load', function (e) {
          e.preventDefault();
          if (window?.BMap && window?.BMap.Map) {
            const event = new CustomEvent('BAIDU_SCRIPT_LOADED', { detail: { loaded: true } });

            document.dispatchEvent(event);
          }
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getBaiduMap = () => {
    const mapele = document.querySelector('.chineseContent.loading-map') as HTMLAnchorElement;
    const BMap = window?.BMap;
    if (mapele) {
      let latlng = [mapele.href];

      if (latlng[0].search('latlng=') >= 0) {
        const substr = latlng[0].substring(latlng[0].search('latlng='));
        latlng = substr ? substr.split('=') : [];
        latlng = latlng && latlng.length > 1 ? latlng[1].split(',') : [];
      }

      const lat = latlng && latlng.length > 1 ? latlng[0] : null;
      const long = latlng && latlng.length > 1 ? latlng[1] : null;

      if (lat && long) {
        const gPoint = new BMap.Point(long, lat);
        type BMapTranslateResult = { status: number; points: Array<{ lat: number; lng: number }> };
        const translateCallback = (data: BMapTranslateResult) => {
          if (data.status === 0) {
            let href = mapele.href.split('?')[0];
            href = `${href}?latlng=${data.points[0].lat},${data.points[0].lng}`;
            mapele.href = href;
            mapele.classList.remove('loading-map');
          }
        };

        const convertor = new BMap.Convertor();
        const points = [];
        points.push(gPoint);
        convertor.translate(points, 3, 5, translateCallback);
      }
    }
  };

  useEffect(() => {
    const location = datalayer?.['browser_akamai_loc_country']
      ? (datalayer?.['browser_akamai_loc_country'] as string)
      : '';
    if (
      location &&
      chineseLocations.includes(location) &&
      // adding this condition to prevent this block from executing in case of datalayer update
      !document.querySelector('body')?.classList.contains('chinese-page')
    ) {
      document.querySelector('body')?.classList.add('chinese-page');
      baiduMap();
    }
  }, [datalayer]);

  useEffect(() => {
    document.addEventListener('BAIDU_SCRIPT_LOADED', function () {
      if (window?.BMap && window.BMap?.Map) {
        getBaiduMap();
      }
      document.removeEventListener('BAIDU_SCRIPT_LOADED', getBaiduMap);
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!isMobileAndAboveViewPort) {
        if (bookYourStayButtonRef && bookYourStayButtonRef.current) {
          if (window.scrollY > 0) {
            bookYourStayButtonRef.current.style.display = 'block';
          } else {
            bookYourStayButtonRef.current.style.display = 'none';
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileAndAboveViewPort]);

  return (
    <>
      <div className="local-header__menu-top-left d-flex flex-wrap py-2 py-md-3 align-items-center col-12 col-md-5 p-0">
        <a
          href={pageProps?.homePage}
          target={
            preProcessorResponse?.sameTab || preProcessorResponse?.cna
              ? '_self'
              : pageProps?.openHotelLogoInNewTab === 'true'
              ? '_blank'
              : '_self'
          }
          rel="noreferrer"
        >
          <Image
            defaultImageURL={pageProps?.hotelLogoFile}
            altText={pageProps?.logoAltText}
            dynamic={true}
            renditions={pageProps?.dynamicMedia?.renditions ?? []}
            title={pageProps?.logoAltText || 'Hotel logo'}
            loading="eager"
            customClass="local-header__menu-top-left--logo"
          ></Image>
        </a>
        <div className="d-flex flex-wrap col">
          <p className="local-header__menu-top-left--title pl-xl-1 pr-xl-1 pl-0 mb-0 t-subtitle-xl">
            {data?.property?.basicInformation?.name}
          </p>
        </div>
      </div>
      <div
        className={`local-header__menu-top-right d-flex py-2 py-md-3 align-items-center justify-content-around ${
          showAccuWeatherInL1 ? 'col-12' : 'col-9'
        } col-md-auto px-0`}
      >
        <div className="local-header__menu-top-right-location d-flex align-items-center">
          {!pageProps?.hideMap && data?.property?.basicInformation?.name && (
            <>
              <span className="local-header__menu-top-right-location-icon  icon-location t-accent-color"></span>
              <a
                className="local-header__menu-top-right-location-link local-header__menu-top-links custom_click_track ml-2 pl-1 pl-md-0 pr-md-4 pr-xl-0 mr-md-1 mr-xl-0 ml-md-3 t-font-s nonChineseContent"
                data-custom_click_track_value={`${analyticsTrackingIdentifier?.viewMap}|${analyticsTrackingUniqueDescription?.mapCtaLabel}|external|`}
                tabIndex={0}
                target={
                  preProcessorResponse?.sameTab || preProcessorResponse?.cna
                    ? '_self'
                    : pageProps?.openMapInNewTab === 'true'
                    ? '_blank'
                    : '_self'
                }
                href={`${mapURL}?api=1&query=${data?.property?.basicInformation?.name?.replaceAll(' ', '+')}`}
                rel="noreferrer"
              >
                <span className="view-map-desktop d-none d-xl-inline-block">{pageProps?.mapCtaLabel}</span>
                <span className="view-map-mobile d-inline-block d-xl-none">{pageProps?.mobileMapCtaLabel}</span>
              </a>
              <a
                className="local-header__menu-top-right-location-link local-header__menu-top-links custom_click_track ml-2 pl-1 pl-md-0 pr-md-4 pr-xl-0 mr-md-1 mr-xl-0 ml-md-3 t-font-s chineseContent loading-map"
                data-custom_click_track_value={`${analyticsTrackingIdentifier?.viewMap}|${analyticsTrackingUniqueDescription?.mapCtaLabel}|external|`}
                tabIndex={0}
                target={
                  preProcessorResponse?.sameTab || preProcessorResponse?.cna
                    ? '_self'
                    : pageProps?.openMapInNewTab === 'true'
                    ? '_blank'
                    : '_self'
                }
                href={`${baiduMapURL}?latlng=${data?.property?.basicInformation?.latitude},${data?.property?.basicInformation?.longitude}`}
                rel="noreferrer"
              >
                <span className="view-map-desktop d-none d-xl-inline-block">{pageProps?.mapCtaLabel}</span>
                <span className="view-map-mobile d-inline-block d-xl-none">{pageProps?.mobileMapCtaLabel}</span>
              </a>
            </>
          )}
        </div>
        <div className="local-header__menu-top-right-separator d-lg-none"></div>
        <div
          className={clsx(
            'local-header__menu-top-right-phone pl-xl-3 d-flex align-items-center custom_click_track',
            `${!showAccuWeatherInL1 ? ' ml-xl-5' : ''}`
          )}
          data-custom_click_track_value={`${analyticsTrackingIdentifier?.phoneNumber}|Phone Number|external|`}
        >
          {data && data?.property?.contactInformation?.contactNumbers?.[0]?.phoneNumber?.display && (
            <>
              <span className="local-header__menu-top-right-phone-icon icon-phone t-accent-color pl-md-3 pl-xl-0"></span>
              <a
                className="local-header__menu-top-right-phone-link local-header__menu-top-links custom_click_track ml-2 pl-1 pl-md-0 ml-md-3 t-font-s"
                tabIndex={0}
                href={`tel:${data?.property?.contactInformation?.contactNumbers?.[0].phoneNumber?.display}`}
                data-custom_click_track_value={`${analyticsTrackingIdentifier?.phoneNumber}|Phone Number|external|`}
              >
                {convertPhoneNumberToDE(data?.property?.contactInformation?.contactNumbers?.[0]?.phoneNumber?.display)}
              </a>
            </>
          )}
        </div>
        <div className="local-header__menu-top-right-separator d-lg-none"></div>
        {showAccuWeatherInL1 ? (
          <div className="local-header__menu-top-right-accu-weather pl-xl-3 d-flex align-items-center">
            <div
              className="accu-weather-fragment rendered"
              data-id="headerAccuweather"
              id="headerAccuweather"
              data-compid="headerAccuweather"
            >
              <LocalHeaderAccuWeather
                {...pageProps}
                showAccuWeatherInL1={showAccuWeatherInL1}
                isAuthorMode={pageProps?.isAuthorMode}
                isInEditor={pageProps?.isInEditor}
              />
            </div>
          </div>
        ) : null}
        <div className="local-header__menu-top-reserve p-3 p-xl-0 p-md-3 p-xl-0 ml-xl-3" ref={bookYourStayButtonRef}>
          <Link
            linkHref={pageProps?.buttonLink ?? ''}
            linkClassName="local-header__menu-top-reserve_button m-button-s m-button-secondary ml-0 d-flex align-items-center justify-content-center col-auto col-xl-auto py-0 my-0 my-md-3 my-xl-0 mr-0 mr-md-3 mr-xl-0 custom_click_track"
            target={
              preProcessorResponse?.sameTab || preProcessorResponse?.cna
                ? '_self'
                : pageProps?.openButtonLinkInNewTab === 'true'
                ? '_blank'
                : '_self'
            }
            rel="noreferrer"
            custom_click_track_value={`${analyticsTrackingIdentifier?.bookYourNextStay}|${analyticsTrackingUniqueDescription.bookYourNextStayButtonLabel}|internal|`}
          >
            {pageProps?.buttonLabel}
          </Link>
        </div>
      </div>
    </>
  );
};
